import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionTemplate, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowRight, Ticket } from "lucide-react";
import { api } from "@/lib/api";
import { useAutoRefresh } from "@/lib/live";

const HERO_CARD = "https://images.unsplash.com/photo-1621568670868-24a7dfc590e9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwzfHx0cmFkaW5nJTIwY2FyZHMlMjBjb2xsZWN0aW9ufGVufDB8fHx8MTc4OTE0MTM1M3ww&ixlib=rb-4.1.0&q=85";
const VAULT_BG = "https://images.unsplash.com/photo-1645380192916-cf0057dbc339?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHw0fHx2YXVsdCUyMHNhZmUlMjBkYXJrfGVufDB8fHx8MTc4OTE0MTM2Nnww&ixlib=rb-4.1.0&q=85";

const DEFAULT_CARD = {
  name: "Charizard 1st Edition Shadowless",
  sub: "Base Set (1999) • Secret Rare",
  tag: "PSA 9 MINT",
  points: 2850,
  image: HERO_CARD,
  id: null,
};

const lineVar = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { delay: 0.15 + i * 0.14, duration: 0.9, ease: [0.16, 1, 0.3, 1] } }),
};

function TiltCard({ card }) {
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 120, damping: 16 });
  const sry = useSpring(ry, { stiffness: 120, damping: 16 });
  const mx = useMotionValue(50);
  const my = useMotionValue(50);
  const shine = useMotionTemplate`radial-gradient(circle at ${mx}% ${my}%, rgba(255,255,255,0.22), transparent 55%)`;
  const transform = useMotionTemplate`perspective(1000px) rotateX(${srx}deg) rotateY(${sry}deg)`;

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width;
    const py = (e.clientY - r.top) / r.height;
    ry.set((px - 0.5) * 20);
    rx.set(-(py - 0.5) * 20);
    mx.set(px * 100);
    my.set(py * 100);
  };
  const onLeave = () => { rx.set(0); ry.set(0); mx.set(50); my.set(50); };

  const cardBody = (
    <div className="rounded-2xl border border-white/15 bg-gradient-to-b from-zinc-700/50 to-zinc-950 p-3 shadow-[0_30px_80px_rgba(0,0,0,0.9)]">
      <div className="flex items-center justify-between px-2 py-2">
        <span className="font-mono text-[10px] tracking-[0.2em] text-zinc-400 uppercase">Wanted TCG</span>
        <span className="font-mono text-[10px] tracking-widest text-red-500 font-bold">{card.tag}</span>
      </div>
      <div className="relative rounded-lg overflow-hidden border border-white/10">
        <img src={card.image} alt={card.name} className="w-full aspect-[3/4] object-cover" />
        <motion.div style={{ background: shine }} className="absolute inset-0 pointer-events-none" />
      </div>
      <div className="px-2 pt-3 pb-1">
        <p className="font-display font-bold text-white text-sm leading-tight">{card.name}</p>
        <p className="font-mono text-xs text-zinc-500 mt-1">{card.sub}</p>
      </div>
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.6, duration: 1, ease: [0.16, 1, 0.3, 1] }}
      className="relative flex justify-center"
    >
      <div className="absolute inset-0 bg-red-600/20 blur-[120px] rounded-full scale-75" aria-hidden />
      <motion.div
        data-testid="hero-tilt-card"
        onMouseMove={onMove}
        onMouseLeave={onLeave}
        style={{ transform }}
        className="relative w-72 sm:w-80 cursor-pointer will-change-transform"
      >
        {card.id ? (
          <Link to={`/prodotto/${card.id}`} data-testid="hero-card-link" className="block">{cardBody}</Link>
        ) : cardBody}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1.3, duration: 0.5 }}
          className="absolute -right-4 -bottom-4 bg-red-600 text-white font-mono text-xs font-bold px-4 py-2.5 rounded-lg shadow-[0_0_30px_rgba(220,38,38,0.5)]"
        >
          +{Number(card.points || 0).toLocaleString("it-IT")} PUNTI
        </motion.div>
      </motion.div>
    </motion.div>
  );
}

export default function Hero() {
  const { scrollYProgress } = useScroll();
  const bgY = useTransform(scrollYProgress, [0, 0.3], [0, 120]);
  const [card, setCard] = useState(DEFAULT_CARD);

  // La carta della vetrina si sceglie dal pannello admin (Catalogo → stella Hero)
  const loadHeroCard = () => {
    api.get("/hero-product")
      .then((r) => {
        const p = r.data?.product;
        if (p) {
          setCard({
            id: p.id,
            name: p.name,
            sub: [p.set, p.rarity].filter(Boolean).join(" • ") || p.game,
            tag: (p.condition || p.grading || p.game || "").toUpperCase(),
            points: p.points || 0,
            image: p.image || HERO_CARD,
          });
        } else {
          setCard(DEFAULT_CARD);
        }
      })
      .catch(() => {});
  };
  useEffect(() => { loadHeroCard(); }, []);
  useAutoRefresh(loadHeroCard, 60000);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden pt-20">
      <motion.div style={{ y: bgY }} className="absolute inset-0" aria-hidden>
        <img src={VAULT_BG} alt="" className="w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/70 via-[#050505]/85 to-[#050505]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,#050505_75%)]" />
      </motion.div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-8 grid lg:grid-cols-2 gap-16 items-center py-24 w-full">
        <div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.8 }}
            className="text-xs font-mono uppercase tracking-[0.25em] text-red-500 font-semibold mb-6"
          >
            Collectors Store — Torino Lingotto
          </motion.p>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-black uppercase tracking-tighter leading-[0.95] text-white">
            <span className="block overflow-hidden pb-1"><motion.span className="block" variants={lineVar} custom={0} initial="hidden" animate="show">Il caveau dei</motion.span></span>
            <span className="block overflow-hidden pb-1"><motion.span className="block" variants={lineVar} custom={1} initial="hidden" animate="show">collezionisti</motion.span></span>
            <span className="block overflow-hidden pb-2"><motion.span className="block text-red-600" variants={lineVar} custom={2} initial="hidden" animate="show">TCG</motion.span></span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.8 }}
            className="text-xs font-mono uppercase tracking-[0.15em] text-zinc-400 leading-loose mt-6 max-w-lg"
          >
            Il nostro caveau contiene solo il meglio del mondo TCG: carte singole giocabili, carte rare, carte gradate per i più agguerriti collezionisti, ma anche moltissimi prodotti sealed, case delle migliori espansioni e box sigillati.
            <span className="block mt-4">Guadagna punti fedeltà su ogni singolo acquisto: cumula punti sia da acquisti sul nostro store online e sia in negozio.</span>
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.8 }}
            className="flex flex-wrap gap-4 mt-10"
          >
            <Link
              to="/catalogo"
              data-testid="hero-catalogo-cta"
              className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-display font-bold uppercase tracking-wide text-sm px-8 py-4 rounded-full transition-all active:scale-[0.98] hover:shadow-[0_0_30px_rgba(220,38,38,0.4)]"
            >
              Esplora il catalogo <ArrowRight size={16} />
            </Link>
            <Link
              to="/fedelta"
              data-testid="hero-receipt-cta"
              className="inline-flex items-center gap-2 border border-white/25 hover:border-red-600/60 text-white font-display font-bold uppercase tracking-wide text-sm px-8 py-4 rounded-full transition-all active:scale-[0.98]"
            >
              <Ticket size={16} className="text-red-500" /> Programma fedeltà
            </Link>
          </motion.div>

        </div>

        <TiltCard card={card} />
      </div>
    </section>
  );
}
